// Master output meter — reads the real post-master signal from the engine.
// Shows RMS level, peak (held 1.5 s) and a sticky clip warning (tap to reset).
import React, { useEffect, useRef, useState } from "react";

const toPct = (db) => Math.max(0, Math.min(100, ((db + 60) / 60) * 100));

export function MasterMeter({ engineRef, active }) {
  const [lvl, setLvl] = useState({ rms: -120, peak: -120, hold: -120 });
  const [clip, setClip] = useState(false);
  const holdRef = useRef({ db: -120, at: 0 });
  useEffect(() => {
    if (!active) { setLvl({ rms: -120, peak: -120, hold: holdRef.current.db }); return undefined; }
    let raf;
    let last = 0;
    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (t - last < 66) return; // ~15 fps is plenty for a meter
      last = t;
      const r = engineRef.current?.readOutputLevel?.();
      if (!r) return;
      const h = holdRef.current;
      if (r.peakDb >= h.db || t - h.at > 1500) { h.db = r.peakDb; h.at = t; }
      if (r.peakDb >= -0.1) setClip(true);
      setLvl({ rms: r.rmsDb, peak: r.peakDb, hold: h.db });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, engineRef]);
  const color = lvl.peak >= -0.1 ? "bg-red-500" : lvl.peak > -6 ? "bg-amber-400" : "bg-emerald-400";
  return (
    <button type="button" onClick={() => { setClip(false); holdRef.current = { db: -120, at: 0 }; }}
      className="flex items-center gap-2 w-full px-3 py-1" data-testid="master-output-meter" title="Master output — tap to reset peak/clip"
      data-rms-db={lvl.rms.toFixed(1)} data-peak-db={lvl.hold.toFixed(1)} data-clipped={clip ? "1" : "0"}>
      <span className="text-[9px] uppercase tracking-widest font-bold text-gray-400 w-10 text-left">Out</span>
      <span className="relative flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
        <span className={`absolute inset-y-0 left-0 ${color} transition-[width] duration-75`} style={{ width: `${toPct(lvl.rms)}%` }} />
        <span className="absolute inset-y-0 w-[2px] bg-white" style={{ left: `${toPct(lvl.hold)}%` }} />
      </span>
      <span className="text-[10px] tabular-nums text-gray-300 w-14 text-right" data-testid="master-peak-readout">
        {lvl.hold <= -119 ? "−∞" : `${lvl.hold.toFixed(1)} dB`}
      </span>
      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${clip ? "bg-red-600 text-white" : "bg-white/5 text-gray-600"}`} data-testid="master-clip-indicator">CLIP</span>
    </button>
  );
}
